import MessageContent from "~/components/MessageContent";
import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import { FormProvider } from "@unform/core";
import { v4 as uuid } from "uuid";
import FindGeneric from "~/components/Finds/FindGeneric";
import { useSelector } from "react-redux";
import PageHeader from "~/components/PageHeader";
import Breadcrumbs from "~/components/Breadcrumbs";
import { getCurrentPage } from "~/functions";
import { buttonClass, fieldClass, Field, messagingRequest, errorText, messageSectionAccess } from "./shared";

const InputContext = createContext({ setSuccessfullyUpdated: () => null });
const channelsAvailable = [["email", "Email"], ["sms", "SMS"], ["push", "Mobile push notification"], ["inbox", "Internal app inbox"]];
const channelLabel = channel => channelsAvailable.find(([key]) => key === channel)?.[1] || channel;
const statusLabel = row => row.status === "accepted" ? row.channel === "inbox" ? "Saved in app inbox" : row.channel === "sms" ? "Request accepted by Voxtell — delivery unconfirmed" : "Accepted by provider" : ({ pending: "Pending", failed: "Failed", blocked: "Blocked", unknown: "Unknown outcome — do not retry" }[row.status] || row.status);

export default function DirectInbox() {
  const { filial, accesses } = useSelector(state => state.auth);
  const access = messageSectionAccess(accesses, "messages-quick-message")?.MenuHierarchyXGroup || {};
  return <div className="h-full min-w-0 min-h-0 bg-white flex flex-1 flex-col justify-start items-start rounded-tr-2xl px-4">
    <PageHeader><Breadcrumbs currentPage={getCurrentPage()} /></PageHeader>
    {!access.view || !filial?.id ? <p role="alert" className="p-4 text-sm text-gray-600">{!access.view ? "You do not have permission to view Quick message." : "Select a location to send a message."}</p>
      : !access.create ? <p role="status" className="p-4 text-sm text-gray-600">You do not have permission to send a quick message.</p>
      : <InboxForm key={filial.id} filial={filial} />}
  </div>;
}

function InboxForm({ filial }) {
  const [student, setStudent] = useState({});
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [channels, setChannels] = useState(["inbox"]);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [review, setReview] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const flight = useRef(false);
  const submission = useRef(null);
  const controller = useRef(new AbortController());
  const locked = busy || submitted;
  useEffect(() => {
    controller.current = new AbortController();
    return () => controller.current.abort();
  }, []);
  const request = useCallback((method, url, data) => messagingRequest(filial.id, method, url, data, controller.current.signal), [filial.id]);
  const loadStudents = useCallback(async ({ search, limit, signal }) => {
    try {
      const { data } = await messagingRequest(filial.id, "get", "/messaging/inbox/students", undefined, signal, { search, limit, page: 1 });
      return data.rows.map(row => ({ ...row, display_name: [row.name, row.last_name].filter(Boolean).join(" ") }));
    } catch (failure) { if (!signal.aborted) setError(errorText(failure)); return []; }
  }, [filial.id]);

  function change() { setReview(null); setError(""); }

  function checkedResponse(data) {
    if (Number(data.filial_id) !== Number(filial.id) || data.id !== submission.current?.id || data.student_id !== submission.current?.values.student_id) throw new Error("Response does not match this student and location.");
    return data;
  }

  async function refresh() {
    if (flight.current || !submission.current) return;
    flight.current = true; setBusy(true); setError("");
    try {
      const { data } = await request("get", "/messaging/quick-messages/" + submission.current.id);
      if (!controller.current.signal.aborted) setReport(checkedResponse(data));
    } catch (failure) { if (!controller.current.signal.aborted) setError(errorText(failure)); }
    finally { flight.current = false; if (!controller.current.signal.aborted) setBusy(false); }
  }

  async function send(event) {
    event.preventDefault();
    if (flight.current || submitted || !student.id || !channels.length) return;
    if (review && !review.enabled) return;
    if (review && !window.confirm("Send to " + review.student_name + " (" + review.registration_number + ") via " + review.channels.map(channelLabel).join(", ") + "?")) return;
    const values = { student_id: student.id, subject: channels.length === 1 && channels[0] === "sms" ? "SMS" : subject, body, channels: [...channels].sort() };
    if (!submission.current || JSON.stringify(submission.current.values) !== JSON.stringify(values)) submission.current = { id: uuid(), values };
    const confirming = !!review;
    flight.current = true; setBusy(true); setError("");
    if (confirming) setSubmitted(true);
    try {
      const { data } = await request("post", "/messaging/quick-messages" + (confirming ? "" : "/preview"),
        { id: submission.current.id, ...values, ...(confirming ? { review_token: review.review_token } : {}) });
      if (controller.current.signal.aborted) return;
      if (confirming) { setReport(checkedResponse(data)); setReview(null); }
      else setReview(checkedResponse(data));
    } catch (failure) {
      if (controller.current.signal.aborted) return;
      setReview(null);
      setError(errorText(failure));
      if (confirming) {
        try {
          const { data } = await request("get", "/messaging/quick-messages/" + submission.current.id);
          if (!controller.current.signal.aborted) setReport(checkedResponse(data));
        } catch (lookupFailure) {
          if (!controller.current.signal.aborted) {
            if (lookupFailure.response?.status === 404 && [400, 403, 409, 429].includes(failure.response?.status)) setSubmitted(false);
            else setError("The sending outcome could not be confirmed. Use Refresh sending status before taking any further action. Do not send again.");
          }
        }
      }
    } finally { flight.current = false; if (!controller.current.signal.aborted) setBusy(false); }
  }

  function newMessage() {
    setStudent({}); setSubject(""); setBody(""); setChannels(["inbox"]); setSubmitted(false); setReview(null); setReport(null); setError("");
    submission.current = null; setRevision(value => value + 1);
  }

  return <div className="w-full min-h-0 flex-1 overflow-y-auto py-4">
    <form onSubmit={send} className="w-full space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b pb-3">
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-700">New quick message</h2>
          <p className="text-xs text-gray-600">Location: <strong>{filial.name || filial.alias}</strong> · One student only</p>
        </div>
      </div>
      <fieldset disabled={locked} className="space-y-4">
        <div className="space-y-3 rounded-sm border p-3">
          <h3 className="text-sm font-semibold text-gray-700">Send through</h3>
          <div className="flex flex-wrap gap-6">{channelsAvailable.map(([channel, label]) => <label key={channel} className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={channels.includes(channel)} className="accent-orange-600" onChange={event => { setChannels(current => event.target.checked ? [...current, channel] : current.filter(item => item !== channel)); change(); }} />{label}</label>)}</div>
          <p className="text-xs text-gray-500">Choose one or combine channels. Push alerts the phone; the internal inbox saves a message in the app notification center. Both are separate options.</p>
          {channels.some(channel => channel !== "inbox") && <p className="text-xs text-gray-500">Only registered email addresses, student phone numbers and authorized devices are used.</p>}
        </div>
        <div className="grid items-start gap-6 lg:grid-cols-2">
          <div className="min-w-0 space-y-3 rounded-sm border p-3">
            <p className="text-xs text-gray-600">Find a student by name or registration, then select one recipient.</p>
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              <FormProvider onSubmit={() => null}>
                <FindGeneric key={revision} title="Student" scope="inbox_student" InputContext={InputContext} defaultValue={{}}
                  fields={[{ title: "Full name", name: "display_name" }, { title: "Registration", name: "registration_number" }]}
                  loadOptions={loadStudents} maxLimit={100} readOnly={locked} clearable clearValue={{}}
                  setReturnFindGeneric={value => { setStudent(value); change(); }} />
              </FormProvider>
            </div>
          </div>
          <div className="min-w-0 space-y-4 rounded-sm border p-4">
            <h3 className="text-sm font-semibold text-gray-700">Message</h3>
            {!(channels.length === 1 && channels[0] === "sms") && <Field label="Subject / notification title"><input required maxLength={255} className={fieldClass} value={subject} onChange={event => { setSubject(event.target.value); change(); }} /></Field>}
            <Field label="Message"><textarea required maxLength={channels.includes("sms") ? 1600 : 20000} rows={8} className={fieldClass} value={body} onChange={event => { setBody(event.target.value); change(); }} /></Field>
            {channels.includes("sms") && <p className="text-xs text-gray-500">SMS sends only the message text from the number configured for this location to the registered student phone. Maximum 1600 characters after personalization; the carrier may split the text into multiple SMS segments.</p>}
            {channels.includes("push") && <p className="text-xs text-gray-500">Use a short title and message when sending a mobile push notification.</p>}
          </div>
        </div>
      </fieldset>
      {review && !submitted && <div role="status" className="space-y-3 rounded-sm border p-3 text-sm">
        <strong>Review before sending — no message has been sent</strong>
        <p>One student: {review.student_name} · {review.registration_number} · {filial.name || filial.alias}</p>
        {review.recipients.filter(row => row.status === "blocked").map((row, index) => <p key={row.channel + index} role="alert" className="text-red-700">{channelLabel(row.channel)} · Blocked: {row.error_message}</p>)}
        {review.recipients.map((row, index) => <div key={row.channel + index} className="space-y-2 border-t pt-3"><p><strong>{channelLabel(row.channel)}</strong> · {row.destination}</p>{row.sms_sender && <p>From: {row.sms_sender.number} · Voxtell user: {row.sms_sender.user}</p>}{row.channel !== "sms" && <strong>{row.content.subject}</strong>}<MessageContent body={row.content.body} /></div>)}
        {!review.enabled && <p role="alert" className="text-red-700">{review.block_reason || "Sending is disabled for a selected channel in this environment."}</p>}
        <button type="button" className={buttonClass} disabled={busy} onClick={() => setReview(null)}>Cancel review</button>
      </div>}
      {!submitted && <button type="submit" className={buttonClass} disabled={!student.id || !channels.length || busy || (review && !review.enabled)}>{review ? "Confirm and send" : "Review message"}</button>}
      {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
      {report && <div role="status" className="space-y-3 rounded-sm border p-3 text-sm">
        <strong>Sending results · {report.student_name} · {report.registration_number}</strong>
        {report.rows.map(row => <div key={row.id} className="space-y-2 border-t pt-3"><p>{channelLabel(row.channel)} · {row.destination}</p>{row.sms_sender && <p>From: {row.sms_sender.number}</p>}<strong>{statusLabel(row)}</strong>{row.channel === "sms" && <div className="text-xs text-gray-600">{row.provider_id && <p>Voxtell reference: {row.provider_id}</p>}{row.provider_response?.session_id && <p>Message session: {row.provider_response.session_id}</p>}{row.provider_response?.http_status && <p>API HTTP status: {row.provider_response.http_status}</p>}</div>}{row.error_message && <p className="text-xs text-red-700">{row.error_message}</p>}</div>)}
        <p className="text-xs text-gray-500">Provider acceptance does not confirm delivery or reading. Failed or unknown outcomes are never automatically retried.</p>
      </div>}
      {submitted && <button type="button" className={buttonClass} disabled={busy} onClick={refresh}>Refresh sending status</button>}
      {report && !report.summary.pending && <button type="button" className={buttonClass} disabled={busy} onClick={newMessage}>New quick message</button>}
    </form>
  </div>;
}
